import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import ShowCustomerInfo from "./ShowCustomerInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReservationItems from "./Reservations/ReservationItems";
import ReservationChat from "./ReservationChat";
import ReservationHistory from "./ReservationHistory";
import Messages from ".//Messages";
import SwitchLabelButton from "./SwitchLabelButton";
import { postPhoneOrEmailConfirmation } from "../actions/reservationsActions";
import ReservationScreen from "../screens/ReservationScreen";
import { ModernButton } from "./DefaultImports";
import ReservationTableNameSelector from "./Reservations/ReservationTableNameSelector";
import ReservationStatusChanger from "./Reservations/ReservationStatusChanger";
import { ConfirmationModal } from "./ConfirmationModal";
import { customerAttendUpdateSlice } from "../reducers/restaurantReducers";
import Tooltip from "./ToolTipWrapper";

import {
  faFilePen,
  faLock,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import {
  NoPhoneSvgIcon,
  PhoneSvgIcon,
  UserSvgIcon,
  NoPhoneTwoLinesSvgIcon,
  NoEmailTwoLinesSvgIcon,
  NoEmailSvgIcon,
  EmailSvgIcon,
} from "../icons/SvgIcons";
import { CustomModal } from "./CustomModal";

const TOP_ICON_COLOR = "#FAF6E9";

/*
  Edit icon sizes here.

  box  = reserved clickable space
  icon = actual icon size inside the box

  Important:
  topHeight is 28px, so header icons should not reserve more than 28px.
*/
const ICON_SIZE_CONFIG = {
  topHeight: 28,
  headerPadding: "0 6px 0 7px",
  headerGap: 6,
  actionGap: 5,

  customer: { box: 28, icon: 20 },
  phone: { box: 28, icon: 24 },
  email: { box: 28, icon: 23 },
  chat: { box: 28, icon: 22 },
  history: { box: 25, icon: 21 },
  edit: { box: 25, icon: 20 },
  lock: { box: 25, icon: 20 },
};

const getIconSize = (iconKey, sizeKey) => {
  return ICON_SIZE_CONFIG?.[iconKey]?.[sizeKey] || 24;
};

const getLayoutSize = (key) => {
  return ICON_SIZE_CONFIG?.[key];
};

const ReservationGrid = styled.div`
  display: grid;
  gap: ${({ $variant }) => ($variant === "floorBoard" ? "1px" : "0")};
  width: 100%;
`;

const ReservationTop = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${getLayoutSize("topHeight")}px;
  background-color: #99b0ad;
  color: ${TOP_ICON_COLOR};
  overflow: hidden;
`;

const ReservationHeaderRow = styled.div`
  width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding: ${getLayoutSize("headerPadding")};
  gap: ${getLayoutSize("headerGap")}px;
`;

const ReservationNameButton = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  color: ${TOP_ICON_COLOR};
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin: 0;
  cursor: inherit;
  overflow: hidden;
`;

const CustomerInfoIconButton = styled.button`
  width: ${getIconSize("customer", "box")}px;
  height: ${getIconSize("customer", "box")}px;
  min-width: ${getIconSize("customer", "box")}px;
  border: none;
  background: transparent;
  color: ${TOP_ICON_COLOR};
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 5px;
  box-sizing: border-box;

  svg {
    width: ${getIconSize("customer", "icon")}px !important;
    height: ${getIconSize("customer", "icon")}px !important;
    max-width: none !important;
    max-height: none !important;
    display: block;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ReservationNameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.92rem;
  font-weight: 750;
  line-height: 1;
`;

const ReservationHeaderActions = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${getLayoutSize("actionGap")}px;
  overflow: visible;
`;

const HeaderIconButton = styled.button`
  width: ${({ $iconKey }) => `${getIconSize($iconKey, "box")}px`};
  height: ${({ $iconKey }) => `${getIconSize($iconKey, "box")}px`};
  min-width: ${({ $iconKey }) => `${getIconSize($iconKey, "box")}px`};
  border: none;
  background: transparent;
  color: ${TOP_ICON_COLOR};
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 1;
  overflow: visible;
  border-radius: 5px;
  box-sizing: border-box;

  svg {
    width: ${({ $iconKey }) => `${getIconSize($iconKey, "icon")}px`} !important;
    height: ${({ $iconKey }) =>
      `${getIconSize($iconKey, "icon")}px`} !important;
    max-width: none !important;
    max-height: none !important;
    display: block;
    color: ${TOP_ICON_COLOR};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const HeaderIconWrap = styled.div`
  width: ${({ $iconKey }) => `${getIconSize($iconKey, "box")}px`};
  height: ${({ $iconKey }) => `${getIconSize($iconKey, "box")}px`};
  min-width: ${({ $iconKey }) => `${getIconSize($iconKey, "box")}px`};
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${TOP_ICON_COLOR};
  overflow: visible;
  border-radius: 5px;
  box-sizing: border-box;

  & > * {
    width: 100%;
    height: 100%;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  button,
  a {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    color: ${TOP_ICON_COLOR} !important;
  }

  svg {
    width: ${({ $iconKey }) => `${getIconSize($iconKey, "icon")}px`} !important;
    height: ${({ $iconKey }) =>
      `${getIconSize($iconKey, "icon")}px`} !important;
    max-width: none !important;
    max-height: none !important;
    display: block;
    color: ${TOP_ICON_COLOR};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ReservationCardStyle = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  border: ${({ $variant }) =>
    $variant === "floorBoard" ? "none" : "1px solid #cbd1cf"};
  border-left: ${({ $selected, $variant }) => {
    if ($variant !== "floorBoard") {
      return $selected ? "5px solid #ee754c" : "1px solid #cbd1cf";
    }

    return $selected ? "5px solid #ee754c" : "3px solid transparent";
  }};
  border-radius: ${({ $variant }) => ($variant === "floorBoard" ? "0" : "4px")};
  padding: 0;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  max-height: ${({ $variant }) =>
    $variant === "floorBoard" ? "none" : "400px"};
  margin: 0;
  cursor: ${({ $variant }) =>
    $variant === "floorBoard" ? "pointer" : "default"};
  box-shadow: none;
  transition:
    border-left-color 0.15s ease,
    border-left-width 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-left-color: ${({ $variant }) =>
      $variant === "floorBoard" ? "#ee754c" : "#cbd1cf"};
    background-color: ${({ $variant }) =>
      $variant === "floorBoard" ? "#fffaf7" : "white"};
  }
`;

const ReservationCard = React.memo(
  ({
    reservations,
    reservations_complete,
    isShowCustomerInfo = false,
    isNotifications = false,
    isReservationFromRestaurantScreen = false,
    dispatch_reservation_wait = 0,
    past_estimated_time = false,
    variant = "default",
    selectedReservationId = null,
    onReservationSelect,
  }) => {
    const dispatch = useDispatch();

    const [isShowCustomerInfoModalActive, setIsShowCustomerInfoModalActive] =
      useState(false);
    const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
    const [isCreatingReservation, setIsCreatingReservation] = useState(false);
    const [tokens, setTokens] = useState("");
    const [reservation_or_waiting_list, setReservation_or_waiting_list] =
      useState("reservation");

    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [phoneIsConfirmed, setPhoneIsConfirmed] = useState(false);
    const [completePhone, setCompletePhone] = useState("");

    const [current_reservation_id, setCurrent_reservation_id] = useState("");
    const [customer_name, setCustomer_name] = useState("");

    const manager_inf = useSelector((state) => state.crudManager);
    const { managerInfo } = manager_inf;

    const reservationsFromRestaurant = useSelector(
      (state) => state.reservationsFromRestaurant,
    );
    const { error } = reservationsFromRestaurant;

    const customerAttendUpdates = useSelector(
      (state) => state.customerAttendUpdate,
    );
    const { errorChangeTableNumber } = customerAttendUpdates;

    const restaurant_info = useSelector((state) => state.restaurantDetails);
    const { restaurants } = restaurant_info;

    const reservationZone = useSelector((state) => state.reservationZone);
    const { zones } = reservationZone;

    const all_tables = useSelector((state) => state.restaurantTables.tables);

    const isFloorBoardVariant = variant === "floorBoard";

    /*
      Normal reservation screen:
        isReservationFromRestaurantScreen === true
        => do NOT show before/after history fields inside ReservationItems.

      History/activity usage:
        isReservationFromRestaurantScreen === false
        => ReservationItems can show before/after fields.
    */
    const shouldShowHistoryFields = !isReservationFromRestaurantScreen;

    const reservationCardRefs = useRef({});

    useEffect(() => {
      if (!isFloorBoardVariant || !selectedReservationId) return;

      const element =
        reservationCardRefs.current[String(selectedReservationId)];

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [isFloorBoardVariant, selectedReservationId]);

    const [reservationTokenToPhoneLink, setReservationTokenToPhoneLink] =
      useState("");

    const [reservationHasBeenModified, setReservationHasBeenModified] =
      useState(false);

    const [
      modalReservationModifyReservation,
      setModalReservationModifyReservation,
    ] = useState(false);

    const handleCardSelect = (event, reservation) => {
      if (!isFloorBoardVariant) return;

      const interactiveTarget = event.target.closest(
        'button, a, input, select, textarea, label, [role="button"], [data-prevent-card-select="true"]',
      );

      if (interactiveTarget) return;

      if (typeof onReservationSelect === "function") {
        onReservationSelect(reservation);
      }
    };

    const handleSetPhoneNumber = (
      country_code,
      phone_number,
      reservationID,
      isPhoneConfirmed,
      the_customer_name,
      reservation_token,
    ) => {
      const countryCode = country_code || "";
      const phoneNumber = phone_number || "";
      const countryCodeLength = countryCode.length;

      const formattedPhoneNumber = phoneNumber.startsWith(countryCode)
        ? countryCode + " " + phoneNumber.slice(countryCodeLength)
        : phoneNumber;

      setCompletePhone(formattedPhoneNumber);
      setShowPhoneModal(!showPhoneModal);
      setCurrent_reservation_id(reservationID);
      setPhoneIsConfirmed(isPhoneConfirmed);
      setCustomer_name(the_customer_name);
      setReservationTokenToPhoneLink(reservation_token);
    };

    const UpdatePhoneNumber = () => {
      dispatch(postPhoneOrEmailConfirmation(current_reservation_id, "phone"));
    };

    const showCustomerInfo = (country_code, phone_number) => {
      setCompletePhone(phone_number || "");
      setIsShowCustomerInfoModalActive(true);
    };

    const handleOpenEditReservationModal = (tokeni) => {
      setIsReservationModalOpen(true);
      setIsCreatingReservation(false);
      setReservation_or_waiting_list("reservation");
      setTokens(tokeni);
    };

    const handleOpenEmailEdit = (reservation_token) => {
      handleOpenEditReservationModal(reservation_token);
    };

    const handleCloseReservationModal = () => {
      setIsReservationModalOpen(false);
      setTokens();
    };

    return (
      <ReservationGrid $variant={variant}>
        {reservations && reservations.length > 0 && restaurants ? (
          reservations.map((reservation) => {
            const isSelectedInFloorBoard =
              isFloorBoardVariant &&
              Number(selectedReservationId) === Number(reservation.id);

            const customerIconSize = getIconSize("customer", "icon");
            const phoneIconSize = getIconSize("phone", "icon");
            const emailIconSize = getIconSize("email", "icon");

            return (
              <ReservationCardStyle
                key={reservation.id}
                $variant={variant}
                $selected={isSelectedInFloorBoard}
                ref={(element) => {
                  if (element) {
                    reservationCardRefs.current[String(reservation.id)] =
                      element;
                  }
                }}
                onClick={(event) => handleCardSelect(event, reservation)}
              >
                <ReservationTop
                  status={reservation.status}
                  $variant={variant}
                  $selected={isSelectedInFloorBoard}
                >
                  <ReservationHeaderRow $variant={variant}>
                    <ReservationNameButton>
                      <CustomerInfoIconButton
                        type="button"
                        title="Ver informação do cliente"
                        onClick={(event) => {
                          event.stopPropagation();
                          showCustomerInfo(
                            reservation.country_code,
                            reservation.phone_number,
                          );
                        }}
                      >
                        <UserSvgIcon
                          width={customerIconSize}
                          height={customerIconSize}
                          strokeColor={TOP_ICON_COLOR}
                          style={{
                            minWidth: `${customerIconSize}px`,
                          }}
                        />
                      </CustomerInfoIconButton>

                      <ReservationNameText>
                        {reservation.full_name}
                      </ReservationNameText>
                    </ReservationNameButton>

                    <ReservationHeaderActions>
                      {!isShowCustomerInfo && (
                        <>
                          {!reservation.phone_number ||
                          reservation.phone_number.trim() === "" ||
                          reservation.phone_number.trim().length <= 3 ? (
                            <HeaderIconButton
                              type="button"
                              title="Sem número de telefone"
                              $iconKey="phone"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSetPhoneNumber(
                                  reservation.country_code,
                                  reservation.phone_number,
                                  reservation.id,
                                  reservation.is_phone_confirmed,
                                  reservation.full_name,
                                  reservation.token,
                                );
                              }}
                            >
                              <NoPhoneTwoLinesSvgIcon
                                width={phoneIconSize}
                                height={phoneIconSize}
                                strokeColor={TOP_ICON_COLOR}
                              />
                            </HeaderIconButton>
                          ) : reservation.is_phone_confirmed ? (
                            <HeaderIconButton
                              type="button"
                              title="Telefone confirmado"
                              $iconKey="phone"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSetPhoneNumber(
                                  reservation.country_code,
                                  reservation.phone_number,
                                  reservation.id,
                                  reservation.is_phone_confirmed,
                                  reservation.full_name,
                                  reservation.token,
                                );
                              }}
                            >
                              <PhoneSvgIcon
                                width={phoneIconSize}
                                height={phoneIconSize}
                                strokeColor={TOP_ICON_COLOR}
                              />
                            </HeaderIconButton>
                          ) : (
                            <HeaderIconButton
                              type="button"
                              title="Telefone não confirmado"
                              $iconKey="phone"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSetPhoneNumber(
                                  reservation.country_code,
                                  reservation.phone_number,
                                  reservation.id,
                                  reservation.is_phone_confirmed,
                                  reservation.full_name,
                                  reservation.token,
                                );
                              }}
                            >
                              <NoPhoneSvgIcon
                                width={phoneIconSize}
                                height={phoneIconSize}
                                strokeColor={TOP_ICON_COLOR}
                              />
                            </HeaderIconButton>
                          )}

                          {!reservation.email ||
                          reservation.email.trim() === "" ? (
                            <HeaderIconButton
                              type="button"
                              title="Adicionar email"
                              $iconKey="email"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenEmailEdit(reservation.token);
                              }}
                            >
                              <NoEmailTwoLinesSvgIcon
                                width={emailIconSize}
                                height={emailIconSize}
                                strokeColor={TOP_ICON_COLOR}
                              />
                            </HeaderIconButton>
                          ) : reservation.is_email_confirmed ? (
                            <HeaderIconButton
                              type="button"
                              title="Editar email"
                              $iconKey="email"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenEmailEdit(reservation.token);
                              }}
                            >
                              <EmailSvgIcon
                                width={emailIconSize}
                                height={emailIconSize}
                                strokeColor={TOP_ICON_COLOR}
                              />
                            </HeaderIconButton>
                          ) : (
                            <HeaderIconButton
                              type="button"
                              title="Editar email não confirmado"
                              $iconKey="email"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenEmailEdit(reservation.token);
                              }}
                            >
                              <NoEmailSvgIcon
                                width={emailIconSize}
                                height={emailIconSize}
                                strokeColor={TOP_ICON_COLOR}
                              />
                            </HeaderIconButton>
                          )}
                        </>
                      )}

                      <HeaderIconWrap
                        $iconKey="chat"
                        onClick={(event) => event.stopPropagation()}
                        data-prevent-card-select="true"
                      >
                        <ReservationChat reservation={reservation} />
                      </HeaderIconWrap>

                      {zones && (
                        <HeaderIconWrap
                          $iconKey="history"
                          onClick={(event) => event.stopPropagation()}
                          data-prevent-card-select="true"
                        >
                          <ReservationHistory
                            reservationHistory={reservation.history}
                            timezone={restaurants.timezone}
                            all_tables={all_tables}
                            zones_with_id={zones.zones_with_id}
                          />
                        </HeaderIconWrap>
                      )}

                      {(managerInfo && managerInfo.user.name === "Admin") ||
                      (new Date(reservation.date) >=
                        new Date(
                          new Date().setDate(new Date().getDate() - 2),
                        ) &&
                        managerInfo &&
                        managerInfo.edit_reservations) ||
                      !managerInfo ? (
                        <HeaderIconButton
                          type="button"
                          title="Editar reserva"
                          $iconKey="edit"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenEditReservationModal(reservation.token);
                          }}
                        >
                          <FontAwesomeIcon icon={faFilePen} />
                        </HeaderIconButton>
                      ) : (
                        <HeaderIconButton
                          type="button"
                          title="Reserva bloqueada"
                          $iconKey="lock"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <FontAwesomeIcon icon={faLock} />
                        </HeaderIconButton>
                      )}
                    </ReservationHeaderActions>
                  </ReservationHeaderRow>
                </ReservationTop>

                <Row style={{ width: "100%", padding: "0", margin: "0" }}>
                  <Col
                    xs={12}
                    md={isNotifications ? 12 : isShowCustomerInfo ? 12 : 7}
                    style={{ width: "100%", padding: "0", margin: "0" }}
                  >
                    <ReservationItems
                      reservation={reservation}
                      all_tables={all_tables}
                      previousReservationFromHistory={shouldShowHistoryFields}
                      isReservationFromRestaurantScreen={
                        isReservationFromRestaurantScreen
                      }
                    />
                  </Col>

                  <Col
                    xs={12}
                    style={{ width: "100%", padding: "0", margin: "0" }}
                  >
                    <ReservationStatusChanger
                      past_estimated_time={past_estimated_time}
                      reservation={reservation}
                      index={reservation.id}
                      setTokens={setTokens}
                      isShowCustomerInfo={isShowCustomerInfo}
                    />
                  </Col>

                  <ReservationTableNameSelector
                    reservation={reservation}
                    reservations={reservations_complete}
                    dispatch_reservation_wait={dispatch_reservation_wait}
                  />
                </Row>

                {String(reservation?.restaurant_note || "").length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      right: "5px",
                      bottom: "39px",
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Tooltip text={"Nota: " + reservation.restaurant_note}>
                      <FontAwesomeIcon
                        icon={faNoteSticky}
                        size="xl"
                        style={{ color: "#99B0AD", marginRight: "4px" }}
                      />
                    </Tooltip>
                  </div>
                )}
              </ReservationCardStyle>
            );
          })
        ) : error ? (
          <Messages variant="error">{error}</Messages>
        ) : (
          <p>Não foi encontrada nenhuma reserva para os filtros definidos.</p>
        )}

        <CustomModal
          show={showPhoneModal}
          onHide={() => setShowPhoneModal(false)}
          title="Telefonar para cliente"
          centered={false}
          footer={
            <>
              {completePhone.length > 4 ? (
                <a
                  href={`tel:${"+" + completePhone}`}
                  style={{ cursor: "pointer", textDecoration: "none" }}
                >
                  <ModernButton color="#99B0AD" fontSize="14px">
                    Telefonar
                  </ModernButton>
                </a>
              ) : (
                <ModernButton
                  color="#99B0AD"
                  fontSize="14px"
                  style={{ border: "none" }}
                  disabled={true}
                >
                  Telefonar
                </ModernButton>
              )}

              <ModernButton
                color="#EE754C"
                fontSize="14px"
                onClick={() =>
                  handleOpenEditReservationModal(reservationTokenToPhoneLink)
                }
              >
                Editar número de telemóvel
              </ModernButton>
            </>
          }
        >
          <div style={{ paddingTop: "5px" }}>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "12px",
                color: "#9FBBC1",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>{customer_name}</span>
              {completePhone.length > 4 && (
                <span>
                  +{completePhone.slice(0, 3)} {completePhone.slice(3)}
                </span>
              )}
            </div>

            {completePhone.length <= 4 && (
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#999",
                  marginBottom: "12px",
                }}
              >
                Sem número de telemóvel associado
              </div>
            )}

            {completePhone.length > 4 && (
              <div style={{ marginTop: "5px" }}>
                <SwitchLabelButton
                  leftText="Não confirmado"
                  rightText="Confirmado"
                  onToggle={UpdatePhoneNumber}
                  defaultState={phoneIsConfirmed}
                />
              </div>
            )}
          </div>
        </CustomModal>

        {!isShowCustomerInfo && restaurants?.id && (
          <ShowCustomerInfo
            phone_number={completePhone}
            restaurant_id={restaurants.id}
            isModalActive={isShowCustomerInfoModalActive}
            setIsModalActive={setIsShowCustomerInfoModalActive}
          />
        )}

        {isReservationModalOpen && restaurants?.id && (
          <CustomModal
            show={isReservationModalOpen}
            scrollable
            onHide={() => {
              if (reservationHasBeenModified) {
                setModalReservationModifyReservation(true);
              } else {
                setIsReservationModalOpen(false);
              }
            }}
            title={isCreatingReservation ? "Nova reserva" : "Editar reserva"}
            centered={false}
            body={
              <ReservationScreen
                from_restaurant={true}
                creating={isCreatingReservation}
                setCreating={setIsCreatingReservation}
                isEditModalOpen={isReservationModalOpen}
                setIsEditModalOpen={setIsReservationModalOpen}
                restaurantId={restaurants.id}
                handleCloseReservationModal={handleCloseReservationModal}
                token_from_restaurant={tokens}
                setTokens={setTokens}
                reservation_or_waiting_list={reservation_or_waiting_list}
                setReservationHasBeenModified={setReservationHasBeenModified}
                reservationHasBeenModified={reservationHasBeenModified}
                modalReservationModifyReservation={
                  modalReservationModifyReservation
                }
                setModalReservationModifyReservation={
                  setModalReservationModifyReservation
                }
                no_border={true}
              />
            }
          />
        )}

        <ConfirmationModal
          show={errorChangeTableNumber}
          onHide={() => {
            dispatch(
              customerAttendUpdateSlice.actions.resetChangeTableNumber(),
            );
          }}
          title="Erro"
          body={<>Por favor tenta novamente.</>}
          confirmLabel={<>ok</>}
          confirmColor="#EE754C"
          onConfirm={() => {
            dispatch(
              customerAttendUpdateSlice.actions.resetChangeTableNumber(),
            );
          }}
        />
      </ReservationGrid>
    );
  },
);

export default ReservationCard;
